import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addToast } from '@heroui/toast';
import { IconX as X, IconPlus as Plus, IconTrash as Trash } from '@tabler/icons-react';
import { fetchAdminOrganization, updateAdminOrganization, type AdminOrganization } from '@/api/adminApi';
import { btnAccent, btnGhost, input, label } from '@/components/ui/design';
import { money } from './shared';

type Props = { organizerId: string; onClose: () => void };
type FeeTier = { upToPrice: string; feeType: 'percent' | 'fixed'; feeValue: string };
type Role = 'admin' | 'eventmanager' | 'usher';
type Team = Array<{ email: string; role: Role }>;

const TEXT_FIELDS: Array<{ key: keyof AdminOrganization; label: string; multiline?: boolean }> = [
    { key: 'orgName', label: 'Organization name' },
    { key: 'shortName', label: 'Short name' },
    { key: 'profileUrl', label: 'Public handle' },
    { key: 'email', label: 'Contact email' },
    { key: 'phone', label: 'Phone' },
    { key: 'address', label: 'Address' },
    { key: 'about', label: 'About', multiline: true },
    { key: 'instagram', label: 'Instagram' },
    { key: 'twitter', label: 'Twitter' },
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'facebook', label: 'Facebook' },
];

const ROLE_OPTIONS = (
    <>
        <option value="admin">Admin</option>
        <option value="eventmanager">Event manager</option>
        <option value="usher">Usher</option>
    </>
);

const section = 'mb-6 rounded-2xl bg-grey99 p-4 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]';
const heading = 'mb-3 text-sm font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted';
const removeBtn = 'shrink-0 rounded-full text-red-500 ring-1 ring-red-500/25 hover:bg-red-500/10';

export default function OrganizationEditor({ organizerId, onClose }: Props) {
    const queryClient = useQueryClient();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['admin-organization', organizerId],
        queryFn: () => fetchAdminOrganization(organizerId),
    });
    const [text, setText] = useState<Record<string, string>>({});
    const [testMode, setTestMode] = useState(false);
    const [feeType, setFeeType] = useState<'percent' | 'fixed' | 'tiered'>('percent');
    const [feeValue, setFeeValue] = useState('0');
    const [feeLabel, setFeeLabel] = useState('');
    const [feeTiers, setFeeTiers] = useState<FeeTier[]>([]);
    const [taxRate, setTaxRate] = useState('0');
    const [taxMode, setTaxMode] = useState<'added' | 'included'>('included');
    const [team, setTeam] = useState<Team>([]);
    const [newEmail, setNewEmail] = useState('');
    const [newRole, setNewRole] = useState<Role>('usher');

    useEffect(() => {
        if (!data) return;
        setText(Object.fromEntries(TEXT_FIELDS.map(f => [f.key, String(data[f.key] ?? '')])));
        setTestMode(Boolean(data.testMode));
        setFeeType(data.feeType ?? 'percent');
        setFeeValue(String(data.feeValue ?? 0));
        setFeeLabel(data.feeLabel ?? '');
        setFeeTiers((data.feeTiers ?? []).map(t => ({ upToPrice: t.upToPrice === null ? '' : String(t.upToPrice), feeType: t.feeType, feeValue: String(t.feeValue) })));
        setTaxRate(String(data.taxRate ?? 0));
        setTaxMode(data.taxMode ?? 'included');
        setTeam((data.authorizationEmails ?? []).map(m => ({ email: m.email, role: m.role })));
    }, [data]);

    const save = useMutation({
        mutationFn: () => updateAdminOrganization(organizerId, {
            ...text,
            testMode,
            feeType,
            feeValue: Number(feeValue),
            feeLabel,
            feeTiers: feeTiers.map(t => ({ upToPrice: t.upToPrice === '' ? null : Number(t.upToPrice), feeType: t.feeType, feeValue: Number(t.feeValue) })),
            taxRate: Number(taxRate),
            taxMode,
            authorizationEmails: team,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-organizations'] });
            queryClient.invalidateQueries({ queryKey: ['admin-organization', organizerId] });
            queryClient.invalidateQueries({ queryKey: ['admin-report'] });
            addToast({ title: 'Saved', description: 'Organization settings updated.', color: 'success' });
            onClose();
        },
        onError: (error: any) => {
            addToast({ title: 'Could not save', description: error?.response?.data?.message || error?.message || 'Please try again.', color: 'danger' });
        },
    });

    const addMember = () => {
        const email = newEmail.trim().toLowerCase();
        if (!email) return;
        if (team.some(m => m.email === email)) {
            addToast({ title: 'Already on the team', description: email, color: 'warning' });
            return;
        }
        setTeam(prev => [...prev, { email, role: newRole }]);
        setNewEmail('');
    };

    const updateTier = (index: number, patch: Partial<FeeTier>) =>
        setFeeTiers(prev => prev.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));

    return (
        <div className="fixed inset-0 z-[9000] flex justify-end bg-black/50" onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div role="dialog" aria-modal="true" aria-label="Organization settings" className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-nightSurface">
                <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/10">
                    <div className="min-w-0">
                        <h2 className="truncate text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">{data?.orgName || 'Organization'}</h2>
                        {data?.owner?.email && <p className="text-sm text-grey40 dark:text-nightMuted">Owner: {data.owner.email}</p>}
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 items-center justify-center rounded-full ring-1 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/10"><X className="h-4 w-4" /></button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-5">
                    {isLoading && <p className="text-sm text-grey40 dark:text-nightMuted">Loading...</p>}
                    {isError && <p className="text-sm text-red-500">Failed to load the organization.</p>}
                    {data && (
                        <>
                            <div className={section}>
                                <h3 className={heading}>Profile</h3>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    {TEXT_FIELDS.map(field => (
                                        <label key={field.key} className={field.multiline ? 'sm:col-span-2' : ''}>
                                            <span className={label}>{field.label}</span>
                                            {field.multiline ? (
                                                <textarea rows={4} value={text[field.key] ?? ''} onChange={e => setText(prev => ({ ...prev, [field.key]: e.target.value }))} className={`${input} h-auto py-3`} />
                                            ) : (
                                                <input value={text[field.key] ?? ''} onChange={e => setText(prev => ({ ...prev, [field.key]: e.target.value }))} className={input} />
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className={section}>
                                <h3 className={heading}>Fees and tax</h3>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <label><span className={label}>Service fee type</span>
                                        <select value={feeType} onChange={e => setFeeType(e.target.value as typeof feeType)} className={input}>
                                            <option value="percent">Percent</option>
                                            <option value="fixed">Fixed per ticket</option>
                                            <option value="tiered">Tiered</option>
                                        </select>
                                    </label>
                                    {feeType !== 'tiered' && (
                                        <label><span className={label}>{feeType === 'percent' ? 'Fee (%)' : 'Fee per ticket'}</span>
                                            <input type="number" min="0" step="0.01" value={feeValue} onChange={e => setFeeValue(e.target.value)} className={input} />
                                        </label>
                                    )}
                                    <label><span className={label}>Fee label</span><input value={feeLabel} onChange={e => setFeeLabel(e.target.value)} className={input} /></label>
                                </div>
                                {feeType === 'tiered' && (
                                    <div className="mt-4 space-y-2">
                                        <p className="text-xs text-grey40 dark:text-nightMuted">Tiers apply by ticket price, lowest ceiling first. Leave "up to" empty for the catch-all tier.</p>
                                        {feeTiers.map((tier, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                                <input type="number" min="0" placeholder="Up to price" value={tier.upToPrice} onChange={e => updateTier(index, { upToPrice: e.target.value })} className={input} />
                                                <select value={tier.feeType} onChange={e => updateTier(index, { feeType: e.target.value as FeeTier['feeType'] })} className={input}>
                                                    <option value="percent">%</option>
                                                    <option value="fixed">Fixed</option>
                                                </select>
                                                <input type="number" min="0" step="0.01" placeholder="Fee" value={tier.feeValue} onChange={e => updateTier(index, { feeValue: e.target.value })} className={input} />
                                                <button type="button" aria-label="Remove tier" onClick={() => setFeeTiers(prev => prev.filter((_, i) => i !== index))} className={`h-12 w-12 ${removeBtn}`}><Trash className="mx-auto h-4 w-4" /></button>
                                            </div>
                                        ))}
                                        <button type="button" onClick={() => setFeeTiers(prev => [...prev, { upToPrice: '', feeType: 'percent', feeValue: '0' }])} className={`${btnGhost} h-10`}><Plus className="h-4 w-4" /> Add tier</button>
                                    </div>
                                )}
                            </div>

                            <div className={section}>
                                <h3 className={heading}>Team</h3>
                                <ul className="mb-3 space-y-2">
                                    {team.length === 0 && <li className="text-sm text-grey40 dark:text-nightMuted">No authorized users.</li>}
                                    {team.map(member => (
                                        <li key={member.email} className="flex items-center gap-2">
                                            <span className="min-w-0 flex-1 truncate text-sm text-grey10 dark:text-white">{member.email}</span>
                                            <select value={member.role} onChange={e => setTeam(prev => prev.map(m => (m.email === member.email ? { ...m, role: e.target.value as Role } : m)))} className={`${input} h-10 max-w-[160px]`} aria-label={`Role for ${member.email}`}>{ROLE_OPTIONS}</select>
                                            <button type="button" aria-label={`Remove ${member.email}`} onClick={() => setTeam(prev => prev.filter(m => m.email !== member.email))} className={`h-10 w-10 ${removeBtn}`}><Trash className="mx-auto h-4 w-4" /></button>
                                        </li>
                                    ))}
                                </ul>
                                <div className="flex items-center gap-2">
                                    <input type="email" placeholder="Add email" value={newEmail} onChange={e => setNewEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addMember(); } }} className={input} />
                                    <select value={newRole} onChange={e => setNewRole(e.target.value as Role)} className={`${input} max-w-[160px]`} aria-label="New member role">{ROLE_OPTIONS}</select>
                                    <button type="button" onClick={addMember} className={`${btnGhost} h-12 shrink-0`}>Add</button>
                                </div>
                            </div>

                            <div className={section}>
                                <h3 className={heading}>Payments</h3>
                                <label className="flex items-center gap-3 text-sm font-semibold text-grey10 dark:text-white">
                                    <input type="checkbox" checked={testMode} onChange={e => setTestMode(e.target.checked)} className="h-4 w-4" />
                                    Test mode (uses Stripe test keys)
                                </label>
                                <p className="mt-3 text-sm text-grey40 dark:text-nightMuted">Balance {money(data.balance ?? 0)} · Pending {money(data.pendingBalance ?? 0)}</p>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex justify-end gap-3 border-t border-black/[0.06] px-5 py-4 dark:border-white/10">
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                    <button type="button" onClick={() => save.mutate()} disabled={!data || save.isPending} className={btnAccent}>{save.isPending ? 'Saving...' : 'Save changes'}</button>
                </div>
            </div>
        </div>
    );
}
